import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { setModal } from 'slices/modal'
import {
  BILLS_PAYMENT_TYPE,
  CREDIT_CARD_MODAL_STAGE,
  DOWNLOAD_PAID_BILL_MESSAGE,
  MODAL_KEYS,
  PAYMENT_OPTIONS_DATA,
  PAYMENT_TYPE,
  PIX_MODAL_STAGES,
  PIX_PAYMENT_ERROR_MESSAGE,
} from 'app-constants'
import { financialService } from 'services'

export function usePayment(invoiceId, urlFile, isPaid = false) {
  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [options, setOptions] = useState([])
  const [errorMessage, setErrorMessage] = useState('')
  const [success, setSuccess] = useState(false)
  const dispatch = useDispatch()
  const { getPaidBillDownloadLink, generatePaymentLink, getPaymentOptions } = financialService()

  const PAYMENT_METHODS = {
    [PAYMENT_TYPE.PIX]: handlePixPayment,
    [PAYMENT_TYPE.BANK_SLIP]: handleDownload,
    [PAYMENT_TYPE.CARD]: handleCreditCardPayment,
  }

  useEffect(() => {
    async function fetchOptions() {
      try {
        setIsLoading(true)
        const {
          data: { paymentMethods },
        } = await getPaymentOptions(invoiceId)
        setOptions(paymentMethods)
      } catch (error) {
        setHasError(true)
      } finally {
        setIsLoading(false)
      }
    }

    if (invoiceId) fetchOptions()
  }, [invoiceId, getPaymentOptions])

  function filterOptions(option) {
    if (hasError) return option.type === PAYMENT_TYPE.BANK_SLIP

    return options.includes(option.type)
  }

  function getOptions() {
    const availableOptions = PAYMENT_OPTIONS_DATA.filter(filterOptions)
    return availableOptions.map(option => ({
      ...option,
      onClick: PAYMENT_METHODS[option.type],
    }))
  }

  function handleErrorPix() {
    dispatch(
      setModal({
        key: MODAL_KEYS.PIX_PAYMENT,
        data: { isMyBillsPage: true },
        stage: PIX_MODAL_STAGES.ERROR,
      })
    )
    setErrorMessage(PIX_PAYMENT_ERROR_MESSAGE)
  }

  async function handlePixPayment() {
    try {
      setIsLoading(true)

      const {
        data: { qrCodeText: qrCode },
      } = await generatePaymentLink({ invoiceId, paymentMethod: BILLS_PAYMENT_TYPE.PIX })

      if (qrCode) {
        dispatch(setModal({ key: MODAL_KEYS.PIX_PAYMENT, data: { qrCode } }))
      } else {
        handleErrorPix()
      }
    } catch (error) {
      console.error(error)
      handleErrorPix()
    } finally {
      setIsLoading(false)
      setSuccess(true)
    }
  }

  async function handleCreditCardPayment() {
    try {
      setIsLoading(true)
      const params = {
        invoiceId,
        paymentMethod: BILLS_PAYMENT_TYPE.CREDIT_CARD,
      }
      const {
        data: { paymentLink },
      } = await generatePaymentLink(params)

      dispatch(setModal({ key: MODAL_KEYS.CREDIT_CARD, data: { paymentLink } }))
    } catch (error) {
      console.error(error)

      dispatch(setModal({ key: MODAL_KEYS.CREDIT_CARD, stage: CREDIT_CARD_MODAL_STAGE.ERROR }))
    } finally {
      setIsLoading(false)
      setSuccess(true)
    }
  }

  async function handleDownload() {
    if (isPaid) await handleDownloadPaid()
    else handleDownloadUnpaid()
  }

  async function handleDownloadPaid() {
    try {
      setIsLoading(true)

      const { data } = await getPaidBillDownloadLink(invoiceId)

      if (data.url) window.open(data.url)
      else setErrorMessage(DOWNLOAD_PAID_BILL_MESSAGE)
    } catch (error) {
      console.error(error)

      setErrorMessage(DOWNLOAD_PAID_BILL_MESSAGE)
    } finally {
      setIsLoading(false)
    }
  }

  function handleDownloadUnpaid() {
    if (urlFile) window.open(urlFile)
    else setErrorMessage(DOWNLOAD_PAID_BILL_MESSAGE)
  }

  return {
    isLoading,
    hasError,
    errorMessage,
    getOptions,
    success,
  }
}
